/**
 * Initiatives (docs/2026-09-23-initiatives-exploration.md). A lens, not a container:
 * the page holds no work items. It answers who owes what, which contracts are
 * contributing, what the linked systems say, and what has gone quiet.
 */
import { useState } from "preact/hooks";
import { cap, fmtDate, fmtDayFull, fmtStamp, plural } from "@trellis/core";
import type { InitiativeRollup, InitiativeView } from "@trellis/core";
import { Picker } from "../components/picker.js";

export interface InitiativeSummary {
  id: string; name: string; outcome?: string | null; status: string;
  startsOn?: string | null; endsOn?: string | null; leadUpn?: string | null;
  parentId?: string | null;
  programs: { id: string; name: string }[];
}

export function InitiativesScreen({ entries, onOpen, onCreate, programs, notice }: {
  entries: InitiativeSummary[];
  onOpen: (id: string) => void;
  /** Resolves false when the create was refused, so the form keeps what was typed. */
  onCreate?: ((input: { name: string; programId: string; outcome?: string }) => Promise<boolean | void>) | undefined;
  programs: { id: string; name: string }[];
  notice?: { text: string; error?: boolean } | undefined;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [outcome, setOutcome] = useState("");
  const [programId, setProgramId] = useState("");
  const [busy, setBusy] = useState(false);
  const roots = entries.filter((e) => !e.parentId);
  const childrenOf = (id: string) => entries.filter((e) => e.parentId === id);
  return (
    <main class="main settings">
      <div class="page-head">
        <span class="label">Initiatives · the work the contract does not name</span>
        <h1 class="h-lg">What we are pushing</h1>
        <p class="lede">A quarterly push that cuts across workstreams and contracts. Trellis holds the promises and points at the systems that hold the tickets; it does not copy them.</p>
        {notice && <div class={`notice${notice.error ? " error" : ""}`}>{notice.text}</div>}
      </div>

      <section class="panel">
        <div class="panel-head"><span class="label">Open</span></div>
        {roots.length === 0 && <div class="line"><span class="dot off" /><span class="body muted">None yet.</span></div>}
        {roots.map((e) => (
          <div key={e.id}>
            <div class="line">
              <span class={`dot ${e.status === "active" ? "good" : e.status === "landed" ? "navy" : e.status === "dropped" ? "off" : "watch"}`} />
              <span class="body">
                <button type="button" class="link" onClick={() => onOpen(e.id)}>{e.name}</button>
                <div class="s">{[cap(e.status), e.programs.map((p) => p.name).join(" · ") || "no contract yet", e.endsOn ? `through ${fmtDayFull(e.endsOn)}` : ""].filter(Boolean).join(" · ")}</div>
                {e.outcome && <div class="s">{e.outcome}</div>}
              </span>
            </div>
            {childrenOf(e.id).map((k) => (
              <div class="line child" key={k.id}>
                <span class="dot navy" />
                <span class="body">
                  <button type="button" class="link" onClick={() => onOpen(k.id)}>{k.name}</button>
                  <div class="s">{k.programs.map((p) => p.name).join(" · ") || "No contract yet"}</div>
                </span>
              </div>
            ))}
          </div>
        ))}
      </section>

      {onCreate && (
        <section class="panel">
          <div class="panel-head"><span class="label">Start one</span></div>
          {!adding
            ? <button class="link" onClick={() => setAdding(true)}>Add an initiative</button>
            : (
              <div class="inline-form">
                <label class="sr-only" for="new-initiative-name">Name</label>
                <input id="new-initiative-name" value={name} placeholder="Name, e.g. Multi-cloud operationalization" onInput={(e) => setName((e.target as HTMLInputElement).value)} />
                <label class="sr-only" for="new-initiative-outcome">What done means</label>
                <input id="new-initiative-outcome" value={outcome} placeholder="What done means, in a sentence" onInput={(e) => setOutcome((e.target as HTMLInputElement).value)} />
                <Picker label="Starting contract" options={programs.map((p) => ({ value: p.id, label: p.name }))} value={programId} onChange={setProgramId} placeholder="Search contracts…" />
                <div class="buttons">
                  <button class="btn" disabled={busy || !name.trim() || !programId} onClick={() => {
                    setBusy(true);
                    void onCreate({ name: name.trim(), programId, ...(outcome.trim() ? { outcome: outcome.trim() } : {}) })
                      .then((ok) => { if (ok !== false) { setAdding(false); setName(""); setOutcome(""); } })
                      .finally(() => setBusy(false));
                  }}>Create</button>
                  <button class="link" onClick={() => setAdding(false)}>Cancel</button>
                  <span class="muted">You can only start one on a contract you hold a role on. Add the others after.</span>
                </div>
              </div>
            )}
        </section>
      )}
    </main>
  );
}

/**
 * One pointer, with what it last read. A pointer that came back empty and one that was
 * never read look the same from the outside, so the row says which, and it names the
 * projects the query was confined to: that is usually why a good query counts nothing.
 */
function Pointer({ link, programs, onRemove }: {
  link: InitiativeDetail["links"][number];
  programs: InitiativeDetail["programs"];
  onRemove?: ((sourceId: string) => Promise<void>) | undefined;
}) {
  const r = link.rollup;
  const counted = r ? (r.open ?? 0) + (r.inProgress ?? 0) + (r.done ?? 0) : 0;
  const program = programs.find((p) => p.id === link.programId)?.name ?? link.programId;
  const state = link.error ? link.error
    : !link.lastSeen ? "Not read yet · Trellis reads it within fifteen minutes"
    : counted ? <Rollup total={r!} />
    : "Read, and nothing matched";
  return (
    <div class="line">
      <span class={`dot ${link.error ? "bad" : counted ? "good" : link.lastSeen ? "watch" : "off"}`} />
      <span class="body">{link.query}
        <div class="s">{state}</div>
        <div class="s">
          {[link.kind === "jira-epic" ? "Epic" : "Query", program,
            link.scopedTo.length ? `inside ${link.scopedTo.join(", ")}` : "this contract links no ticket project, so it is not read",
            link.lastSeen ? `read ${fmtStamp(link.lastSeen)}` : ""].filter(Boolean).join(" · ")}
        </div>
      </span>
      {onRemove && <span class="row-actions"><button class="btn quiet" onClick={() => void onRemove(link.id)}>Remove</button></span>}
    </div>
  );
}

/** One Teams team read for the initiative, anchored to one of its programs. */
function TeamLink({ link, programs, onRemove }: {
  link: InitiativeDetail["teamLinks"][number];
  programs: InitiativeDetail["programs"];
  onRemove?: ((linkId: string) => Promise<void>) | undefined;
}) {
  const program = programs.find((p) => p.id === link.programId)?.name ?? link.programId;
  const state = link.error ? link.error
    : !link.lastPoll ? "Not read yet · Trellis reads it within ten minutes"
    : typeof link.count === "number" ? `${plural(link.count, "message")} read`
    : "Read, and nothing matched";
  return (
    <div class="line">
      <span class={`dot ${link.error ? "bad" : link.note ? "watch" : link.lastPoll ? "good" : "off"}`} />
      <span class="body">{link.label ?? link.key}
        <div class="s">{state}</div>
        <div class="s">{[link.label ? link.key : "", program, link.lastPoll ? `read ${fmtStamp(link.lastPoll)}` : ""].filter(Boolean).join(" · ")}</div>
        {link.note && !link.error && <div class="s tone-watch">{link.note}</div>}
      </span>
      {onRemove && <span class="row-actions"><button class="btn quiet" onClick={() => void onRemove(link.id)}>Remove</button></span>}
    </div>
  );
}

/** Link a Teams team to the initiative itself. The anchor program decides whose roster,
 * customer register and delivery lead govern what is read. */
function AddTeamLink({ programs, onAdd }: {
  programs: { id: string; name: string }[];
  onAdd: (input: { programId: string; key: string }) => Promise<boolean>;
}) {
  const [programId, setProgramId] = useState("");
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const missing = !programId ? "a contract" : !key.trim() ? "a team id" : "";
  return (
    <div class="inline-form" style="margin:10px 14px 12px">
      <Picker label="Contract" options={programs.map((p) => ({ value: p.id, label: p.name }))} value={programId} onChange={setProgramId} placeholder="Whose roster governs it…" />
      <label class="sr-only" for="init-team-key">Team id</label>
      <input id="init-team-key" value={key} placeholder="Team id" onInput={(e) => setKey((e.target as HTMLInputElement).value)} />
      <button class="btn" disabled={busy || !!missing} onClick={() => {
        setBusy(true);
        void onAdd({ programId, key: key.trim() })
          .then((ok) => { if (ok) setKey(""); })
          .finally(() => setBusy(false));
      }}>Add</button>
      {missing && <span class="hint">Pick {missing} first.</span>}
      <p class="note dim" style="flex-basis:100%;margin:8px 0 0">One team, not one channel: every
        channel in it is read, including channels added later. The Trellis app has to be installed
        on that team before it can read anything.</p>
    </div>
  );
}

function Rollup({ total }: { total: InitiativeRollup }) {
  const parts = [
    total.open ? `${total.open} open` : "",
    total.inProgress ? `${total.inProgress} in progress` : "",
    total.done ? `${total.done} done` : "",
    total.earliestDue ? `from ${fmtDayFull(total.earliestDue)}` : "",
    total.latestDue ? `to ${fmtDayFull(total.latestDue)}` : "",
  ].filter(Boolean);
  if (!parts.length) return <span class="muted">Nothing linked yet.</span>;
  return <>{parts.join(" · ")}</>;
}

export interface InitiativeDetail extends InitiativeView {
  total: InitiativeRollup;
  canManage: boolean;
  attachable: string[];
  links: {
    id: string; programId: string; kind: string; query: string;
    lastSeen: string | null; error: string | null;
    rollup: InitiativeRollup | null;
    scopedTo: string[];
  }[];
  /** A Teams team read for the initiative itself, anchored to one of its programs. */
  teamLinks: {
    id: string; programId: string; kind: string; key: string; label: string | null;
    lastPoll: string | null; count: number | null; error: string | null; note: string | null;
  }[];
}

export function InitiativeScreen({ view, onBack, onStatus, allPrograms, onAttach, onDetach, onAddSource, onRemoveSource, onAddLink, onRemoveLink, instancesOf, notice }: {
  view: InitiativeDetail;
  onBack: () => void;
  onStatus?: ((status: string) => Promise<void>) | undefined;
  /** Contracts the reader holds a role on, so they may put one in. */
  allPrograms?: { id: string; name: string }[];
  onAttach?: ((programId: string) => Promise<void>) | undefined;
  onDetach?: ((programId: string) => Promise<void>) | undefined;
  /** Resolves false when the pointer was refused, so the form keeps what was typed. */
  onAddSource?: ((input: { programId: string; integrationId: string; kind: string; query: string }) => Promise<boolean>) | undefined;
  onRemoveSource?: ((sourceId: string) => Promise<void>) | undefined;
  /** Resolves false when the link was refused, so the form keeps what was typed. */
  onAddLink?: ((input: { programId: string; key: string }) => Promise<boolean>) | undefined;
  onRemoveLink?: ((linkId: string) => Promise<void>) | undefined;
  instancesOf?: ((programId: string) => Promise<{ id: string; displayName: string; kind: string }[]>) | undefined;
  notice?: { text: string; error?: boolean } | undefined;
}) {
  const i = view.initiative;
  const milestones = view.lines.filter((l) => l.milestone);
  const rest = view.lines.filter((l) => !l.milestone);
  const working = view.contracts.filter((c) => c.commitments > 0);
  const people = new Set(view.lines.map((l) => l.owner));
  // Units, not teams: a workstream and a CLIN are units too, and calling them teams
  // would be a small lie on a page whose whole point is that its numbers are exact.
  const involvedUnits = new Set(view.contracts.flatMap((c) => c.units));
  const onIt = new Set(view.programs.map((p) => p.id));
  const canAdd = (allPrograms ?? []).filter((p) => !onIt.has(p.id));
  return (
    <main class="main program settings">
      <div class="this-week page-head">
        <span class="label">Initiative · {i.status}</span>
        <h1 class="h-lg">{i.name}</h1>
        {i.outcome && <p class="lede">{i.outcome}</p>}
        <div class="s">
          {[view.programs.map((p) => p.name).join(" · "), i.endsOn ? `through ${fmtDayFull(i.endsOn)}` : "", view.ruleVersion, view.sources.join(" · ")].filter(Boolean).join(" · ")}
        </div>
        <div class="pick-row"><button class="link" onClick={onBack}>All initiatives</button></div>
      </div>

      {notice && <div class={`notice${notice.error ? " error" : ""}`}>{notice.text}</div>}

      <div class="grid2">
        <section class="panel">
          <div class="panel-head"><span class="label">What the linked systems say</span></div>
          <div class="line"><span class="dot navy" /><span class="body"><Rollup total={view.total} /></span></div>
          {view.links.map((l) => <Pointer key={l.id} link={l} programs={view.programs} {...(onRemoveSource ? { onRemove: onRemoveSource } : {})} />)}
          {view.total.url && <div class="panel-foot"><a class="link" href={view.total.url} rel="noreferrer">Open the tickets</a></div>}
        </section>

        <section class="panel">
          <div class="panel-head"><span class="label">Who is contributing</span></div>
          {working.length === 0 && <div class="line"><span class="dot off" /><span class="body muted">Nobody has a commitment on this yet.</span></div>}
          {working.map((c) => (
            <div class="line" key={c.programId}>
              <span class="dot good" />
              <span class="body">{c.programName}
                <div class="s">{c.people.map((p) => p.name).join(", ")}</div>
              </span>
            </div>
          ))}
          {view.hidden.map((h) => (
            <div class="line" key={h.programId}>
              <span class="dot off" />
              <span class="body muted">{h.count} on {h.programName} you cannot see
                <div class="s">Not yours to read: a candidate, a set-aside or a decline stays with its owner.</div>
              </span>
            </div>
          ))}
          {onStatus && view.canManage && (
            <div class="panel-foot pick-row">
              {["proposed", "active", "landed", "dropped"].filter((st) => st !== i.status).map((st) => (
                <button class="link" key={st} onClick={() => void onStatus(st)}>Mark {st}</button>
              ))}
            </div>
          )}
        </section>
      </div>

      {working.length > 0 && (
        <section class="panel">
          <div class="panel-head"><span class="label">What this costs the contracts</span></div>
          <div class="line">
            <span class="dot navy" />
            <span class="body">
              {plural(view.lines.length, "commitment")} from {plural(people.size, "person", "people")} across {plural(working.length, "contract")}
              {involvedUnits.size > 0 ? `, on ${plural(involvedUnits.size, "unit")}` : ""}
              <div class="s">Counted, not estimated. Trellis carries no hours figure for an initiative: Clockify time is not labelled by initiative, and a number nobody can defend is worse than none (docs/2026-09-24-initiative-hours.md).</div>
            </span>
          </div>
          {working.map((c) => (
            <div class="line" key={c.programId}>
              <span class={`dot ${c.clin === "under-clin" ? "good" : c.clin === "no-clin" ? "bad" : "watch"}`} />
              <span class="body">{c.programName}
                <div class="s">
                  {[
                    plural(c.commitments, "commitment"),
                    `${c.open} open`,
                    `${c.closed} closed`,
                    c.slipped ? `${c.slipped} slipped` : "",
                    plural(c.people.length, "person", "people"),
                    c.units.length ? c.units.join(", ") : "",
                  ].filter(Boolean).join(" · ")}
                </div>
                <div class="s">
                  {c.clin === "under-clin" ? `All of it is under ${c.clins.join(", ")}.`
                    : c.clin === "partly-under-clin" ? `${c.covered} of ${c.classified} classified under ${c.clins.join(", ")}; the rest is under no CLIN.`
                    : c.clin === "no-clin" ? "No CLIN names this work on this contract."
                    : "Not classified onto a unit yet, so whether a CLIN covers it is unknown."}
                  {c.classified < c.commitments && c.classified > 0 ? ` ${c.commitments - c.classified} not classified.` : ""}
                </div>
              </span>
            </div>
          ))}
        </section>
      )}

      <section class="panel">
        <div class="panel-head"><span class="label">Contracts in this initiative</span></div>
        {view.programs.map((p) => (
          <div class="line" key={p.id}>
            <span class={`dot ${p.onIt ? "good" : "navy"}`} />
            <span class="body">{p.name}
              <div class="s">{p.onIt ? "You are on it" : "You can see its confirmed work, not its sources"}</div>
            </span>
            {onDetach && view.attachable.includes(p.id) && (
              <span class="row-actions"><button class="btn quiet" onClick={() => void onDetach(p.id)}>Take out</button></span>
            )}
          </div>
        ))}
        {onAttach && canAdd.length > 0 && <AttachProgram programs={canAdd} onAttach={onAttach} />}
        {onAttach && canAdd.length === 0 && <div class="s">You can only put in a contract you hold a role on.</div>}
      </section>

      {onAddSource && instancesOf && view.attachable.length > 0 && (
        <AddSource programs={view.programs.filter((p) => view.attachable.includes(p.id))} instancesOf={instancesOf} onAdd={onAddSource} />
      )}

      <section class="panel">
        <div class="panel-head"><span class="label">Teams linked to this initiative</span></div>
        {view.teamLinks.length === 0 && <div class="line"><span class="dot off" /><span class="body muted">No team linked.</span></div>}
        {view.teamLinks.map((l) => (
          <TeamLink key={l.id} link={l} programs={view.programs} {...(onRemoveLink ? { onRemove: onRemoveLink } : {})} />
        ))}
        {onAddLink && view.attachable.length > 0 && (
          <AddTeamLink programs={view.programs.filter((p) => view.attachable.includes(p.id))} onAdd={onAddLink} />
        )}
      </section>

      {milestones.length > 0 && (
        <section class="panel">
          <div class="panel-head"><span class="label">Milestones</span></div>
          {milestones.map((l) => <Line key={l.id} line={l} />)}
        </section>
      )}

      <section class="panel">
        <div class="panel-head"><span class="label">Who owes what</span></div>
        {rest.length === 0 && <div class="line"><span class="dot off" /><span class="body muted">No commitments carry this initiative yet.</span></div>}
        {rest.map((l) => <Line key={l.id} line={l} />)}
      </section>
    </main>
  );
}

function Line({ line }: { line: InitiativeDetail["lines"][number] }) {
  return (
    <div class="line">
      <span class={`dot ${line.status === "closed" ? "good" : line.status === "open" ? "navy" : "off"}`} />
      <span class="body">{line.title}
        <div class="s">
          {[line.ownerName, line.programName, `due ${fmtDate(line.dueDate)}`, line.status].join(" · ")}
          {line.acrossContract && <span class="muted"> · another contract</span>}
        </div>
      </span>
    </div>
  );
}

function AttachProgram({ programs, onAttach }: { programs: { id: string; name: string }[]; onAttach: (programId: string) => Promise<void> }) {
  const [programId, setProgramId] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div class="inline-form">
      <Picker label="Put a contract in" options={programs.map((p) => ({ value: p.id, label: p.name }))} value={programId} onChange={setProgramId} placeholder="Search contracts…" />
      <button class="btn" disabled={busy || !programId} onClick={() => { setBusy(true); void onAttach(programId).finally(() => { setBusy(false); setProgramId(""); }); }}>Put it in</button>
      <span class="muted">Only yours to put in. Somebody on that contract has to do it for theirs.</span>
    </div>
  );
}

/** A pointer into the system that holds the work. Counts and dates come back; issues never do. */
function AddSource({ programs, instancesOf, onAdd }: {
  programs: { id: string; name: string }[];
  instancesOf: (programId: string) => Promise<{ id: string; displayName: string; kind: string }[]>;
  onAdd: (input: { programId: string; integrationId: string; kind: string; query: string }) => Promise<boolean>;
}) {
  const [programId, setProgramId] = useState("");
  const [instances, setInstances] = useState<{ id: string; displayName: string; kind: string }[]>([]);
  const [integrationId, setIntegrationId] = useState("");
  const [kind, setKind] = useState("jira-epic");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const missing = !programId ? "a contract" : !integrationId ? "an instance" : !query.trim() ? "a query" : "";
  const ready = !missing;
  const pickProgram = (id: string) => {
    setProgramId(id); setIntegrationId(""); setInstances([]);
    if (id) void instancesOf(id).then((list) => setInstances(list.filter((i) => i.kind === "jira")), () => setInstances([]));
  };
  return (
    <section class="panel">
      <div class="panel-head"><span class="label">Point at where the work is tracked</span></div>
      <div class="inline-form">
        <Picker label="Contract" options={programs.map((p) => ({ value: p.id, label: p.name }))} value={programId} onChange={pickProgram} placeholder="Which contract…" />
        {programId && instances.length === 0 && (
          <span class="hint">That contract links no ticket project yet. Link one on its Integrations tab first, then come back.</span>
        )}
        {instances.length > 0 && (
          <Picker label="Instance" options={instances.map((i) => ({ value: i.id, label: i.displayName }))} value={integrationId} onChange={setIntegrationId} placeholder="Which instance…" />
        )}
        <label class="sr-only" for="src-kind">What kind of pointer</label>
        <select id="src-kind" value={kind} onChange={(e) => setKind((e.target as HTMLSelectElement).value)}>
          <option value="jira-epic">An epic</option>
          <option value="jira-jql">A query</option>
        </select>
        <label class="sr-only" for="src-query">{kind === "jira-epic" ? "Epic key" : "Query"}</label>
        <input id="src-query" value={query} placeholder={kind === "jira-epic" ? "Epic key, e.g. MEPBS-100" : 'JQL, e.g. labels = "multi-cloud"'} onInput={(e) => setQuery((e.target as HTMLInputElement).value)} />
        <button class="btn" disabled={busy || !ready} onClick={() => {
          setBusy(true);
          // Clearing the field only on success: a refused pointer is usually one word wrong,
          // and retyping the whole query is the wrong thing to ask for.
          void onAdd({ programId, integrationId, kind, query: query.trim() })
            .then((ok) => { if (ok) setQuery(""); })
            .finally(() => setBusy(false));
        }}>Point at it</button>
        {missing && <span class="hint">Pick {missing} first.</span>}
        <span class="hint">Trellis reads counts and dates from this. The tickets stay in Jira.</span>
      </div>
    </section>
  );
}
